/*
  Данный regexp был перенесен в валидацию email-адресов контактов справочника телефонов, при внесении изменений
  необходимо внести изменения и в class DenormalizedEmails(EmbeddedDocument): email_address (на данный момент
  находится в /backend/app/telephony/models/phone_directory.py)
*/
export const checkValidEmail = (value: string): boolean => {
  return /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]{1,64}@[a-z0-9]{1,256}\.[a-z]{2,6}$/i.test(
    value
  );
};

// пробная валидация ссылок
export const checkValidURL = (url: string): boolean => {
  // https://www.freecodecamp.org/news/how-to-validate-urls-in-javascript/
  const pattern = new RegExp(
    '^(https?:\\/\\/)' + // протокол
      '((([a-zа-я\\d]([a-zа-я\\d-]*[a-zа-я\\d])*)\\.)+[a-zа-я]{2,}|' + // имя домена
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // IP (v4) адрес
      '(\\:\\d{1,5})?(\\/[-a-z\\d%_.~+]*)*' + // порт и путь //       '(\\:\\d{1,5})?(\\/[-a-z\\d%_.~+?#=]*)*' + // порт и путь
      '(\\?[;&a-zа-я\\d%_.~+=#-]*)?' + // query string
      '(\\#[-a-z\\d_.=]*)?$', // fragment locator, возможно стоит изменить
    'i'
  );

  return pattern.test(url);
};

/*
  Валидация url. Возвращает либо ошибку, либо null при валидной урле.
  Пробная валидация ссылок с выдачей подсказок невалидной части url (переделанная и дополненная checkValidURL())
  Допустима кириллица в домене, пути, query-string параметрах

  Примеры url:
     https://analytics.google.com/analytics/web/provision/?hl=en#/provision
     https://www.figma.com/file/2UDiObhmdC0Rg29aPfV629/Разработка?type=design&node-id=1-4&mode=design
     https://www.avito.ru/sankt-peterburg/knigi_i_zhurnaly/knigi-ASgBAgICAUTOAuoK?cd=1&q=джудит+бек
     https://redmine.eis24.me/issues/445245#note-28
     https://xn----7sbdqbfldlsq5dd8p.xn--p1ai/#/auth/login/
*/
export const getPartialUrlValidationErrMsg = (url: string): string | null => {
  const protocolPattern = new RegExp('^(https?:\\/\\/)', 'i');
  const domainPattern = new RegExp(
    '^((([-a-zа-я\\d]([a-zа-я\\d-]*[a-zа-я\\d])*)\\.)+[a-zа-я\\d-]{2,}|' +
      '((\\d{1,3}\\.){3}\\d{1,3}))(:\\d{1,5})?' + //
      '((/+[-a-z\\d%_.~+#]*)|(\\?[;&a-zа-я\\d%_.~+#=-]*))*$', // путь или query string параметры в пути (например /web/provision/?hl=en#/provision)
    'i'
  );
  const edgeURIPartPattern = new RegExp(
    '^(([-a-zа-я0-9_]+)|' +
      '(([-a-zа-я0-9_]+(\\?[;&a-zа-я0-9%_.~+=#-]+))?' +
      '([-a-zа-я0-9_]*#[-a-z0-9_.=]*)?))$',
    'i'
  );

  if (protocolPattern.test(url)) {
    const urlSplitted = url.split('//');
    const withoutPortPart = urlSplitted[1];
    const withoutPortPartSplitted = withoutPortPart.split('/');
    const lastURIPart =
      withoutPortPartSplitted[withoutPortPartSplitted.length - 1];
    const withoutFragmentLocatorPart = withoutPortPartSplitted
      .slice(0, withoutPortPartSplitted.length - 1)
      .join('/');

    if (
      urlSplitted.length >= 3 ||
      !domainPattern.test(
        withoutPortPartSplitted.length >= 2
          ? withoutFragmentLocatorPart
          : lastURIPart
      )
    ) {
      return 'Необходимо ввести валидное доменное имя или IP-адрес (порт, путь - при необходимости)';
    }

    if (
      withoutPortPartSplitted.length >= 2 &&
      lastURIPart &&
      !edgeURIPartPattern.test(lastURIPart)
    ) {
      return lastURIPart.includes('#') || lastURIPart.includes('?')
        ? 'Возможно введены невалидные query string параметры или fragment locator'
        : 'Путь должен быть валиден';
    }

    return null;
  }

  return 'Ссылка на сайт должна начинаться с http:// или https://';
};

/* Ввод ФИО */
export const validateNameInputValue = (value: string): string => {
  const validValue: string = value
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s/, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-/, '')
    .replace(/[^А-Яа-яЁё0-9\s-]/g, '');

  return validValue?.charAt(0).toUpperCase() + validValue?.slice(1) || '';
};

// Запрет ввода спец символов
export const validateInputValueWithoutSpecialChars = (
  value: string
): string => {
  const onlyNumbersLettersSpacesDash: RegExp = /[^А-Яа-яA-Za-z0-9-\s]/g;

  return value.replaceAll(onlyNumbersLettersSpacesDash, '').trimStart();
};

/* Ввод числовых символов */
export const validateNumberInputValue = (value: string): string => {
  const onlyNumbers: RegExp = /[^0-9]/g;

  return value.replace(onlyNumbers, '');
};

/* Ввод дробных значений */
export const validateFloatInputValue = (
  value: string,
  maxFractionDigits: number = 10
): string => {
  let cleaned = value.replace(/[^0-9.]/g, '');

  const firstDotIndex = cleaned.indexOf('.');

  if (firstDotIndex !== -1) {
    cleaned =
      cleaned.slice(0, firstDotIndex + 1) +
      cleaned
        .slice(firstDotIndex + 1)
        .replace(/\./g, '')
        .slice(0, maxFractionDigits);
  }

  return cleaned;
};

/* Ввод символов, которые могут быть использованы в email */
export const validateMailInputValue = (value: string): string => {
  const onlyValidMailSymbols: RegExp = /[^a-zA-Z0-9._@-]/;

  return value.replace(onlyValidMailSymbols, '');
};

/* Проверяет email на соответствие виду address@domen.highOrderDomen */
export const isValidMail = (value: string): boolean => {
  const email: RegExp = /[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+/;

  return email.test(value);
};

/* Ввод символов, доступных к заполнению создаваемым тегам.
   Без пробелов в начале строки, без возможности ввести 2 пробела подряд,
   только русские буквы, цифры и пробелы. Не более 20 символов. */
export const validateTagInputValue = (value: string): string => {
  return value
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s/, '')
    .replace(/[^А-Яа-я0-9\s]/g, '')
    .slice(0, 20);
};

/* Валидация значения счётчиков. 5 Символов максимум до запятой, 3 после. !dot запрещает ввод дробных чисел */
export const validateMeterInputValue = (
  value: string,
  dot: boolean
): string => {
  const onlyNumbersAndDot: RegExp = /[^0-9.]/g;
  const onlyNumbers: RegExp = /[^0-9]/g;

  let validValue: string = value.replace(
    dot ? onlyNumbersAndDot : onlyNumbers,
    ''
  );

  if (validValue.charAt(0) === '0' && validValue.charAt(1) !== '.') {
    validValue = validValue.split('').slice(1).join();
  }

  return (
    validValue
      .split('.')
      .slice(0, 2)
      .map((item: string, index: number) => {
        return index === 0 ? item.substring(0, 5) : item.substring(0, 3);
      })
      .join('.') || '0'
  );
};

/* Валидация номера документа */
export const validateDocNumber = (value: string): string => {
  const onlyValidDocNameSymbols: RegExp = /[^0-9/-]/g;

  return value.replace(onlyValidDocNameSymbols, '');
};

/* Проверяет номер документа на соответствие виду 111111-07/24 */
export const isValidDocNumber = (value: string): boolean => {
  const docNumber: RegExp = /^[0-9]{6}-[0-9]{2}\/[0-9]{4}$/;

  return docNumber.test(value);
};
